import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { UserPlus } from "lucide-react";
import { Input } from "../components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Signup() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "", church_id: "" });
  const [loading, setLoading] = useState(false);
  const [churches, setChurches] = useState([]);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/churches`).then((r) => setChurches(r.data)).catch(() => {});
  }, []);

  const cls =
    "bg-black border-0 border-b border-white/20 rounded-none focus-visible:ring-0 focus-visible:border-primary h-12 px-0 text-foreground";

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (form.password !== form.confirm) return toast.error("Passwords do not match.");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/signup`, {
        name: form.name,
        email: form.email,
        password: form.password,
        church_id: form.church_id || "",
      });
      setDone(true);
      toast.success("Request submitted. Awaiting admin approval.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-24" data-testid="signup-page">
      <div className="w-full max-w-md">
        <UserPlus className="text-secondary mb-6" size={28} />
        <p className="overline text-primary mb-3">Join The Network</p>
        <h1 className="font-display uppercase text-5xl text-foreground mb-3">Request Account</h1>
        <p className="font-body text-sm text-muted-foreground mb-10">
          Members and church leaders — tell us who you are. An admin will approve your account.
        </p>

        {done ? (
          <div className="border border-secondary/40 p-8 text-center" data-testid="signup-success">
            <p className="font-editorial italic text-xl text-foreground mb-3">Request received.</p>
            <p className="font-body text-sm text-muted-foreground mb-6">
              We’ll notify your church admin. You’ll be able to sign in once approved.
            </p>
            <Link
              to="/admin/login"
              className="overline text-secondary hover:text-foreground"
            >
              ← Back to Sign In
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-6" data-testid="signup-form">
            <Input
              data-testid="signup-name"
              placeholder="Full name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={cls}
              required
            />
            <Input
              data-testid="signup-email"
              type="email"
              placeholder="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className={cls}
              required
            />
            <div>
              <label className="overline text-[0.6rem] text-muted-foreground">Home Church (optional)</label>
              <select
                data-testid="signup-church"
                value={form.church_id}
                onChange={(e) => setForm({ ...form, church_id: e.target.value })}
                className="w-full bg-black border-0 border-b border-white/20 h-12 text-foreground focus:outline-none focus:border-primary font-body"
              >
                <option value="">— Select —</option>
                {churches.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} · {c.city || c.state}
                  </option>
                ))}
              </select>
            </div>
            <Input
              data-testid="signup-password"
              type="password"
              placeholder="Password (min 8 chars)"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className={cls}
              required
            />
            <Input
              data-testid="signup-confirm"
              type="password"
              placeholder="Confirm password"
              value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              className={cls}
              required
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="signup-submit"
              className="w-full bg-primary text-primary-foreground overline py-5 hover:bg-primary/85 transition-colors disabled:opacity-60"
            >
              {loading ? "Submitting..." : "Request Account"}
            </button>
            <div className="text-center pt-4">
              <Link to="/admin/login" className="overline text-secondary hover:text-foreground text-[0.7rem]">
                Already have an account? Sign In →
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
